import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type { ClientModel } from '@/domain/models/client.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import type { FormState } from '@/presentation/pages/projects/project-form/utils/form-state.js';
import type { ProjectStatus } from '@/shared/utils/format-status.js';

interface Props {
    form: FormState;
    errors: Record<string, string>;
    onField: <K extends keyof FormState>(field: K, value: FormState[K]) => void;
    isEdit: boolean;
    clients: ClientModel[];
    selectedClient: ClientModel | null;
    projectStatus: ProjectStatus;
    statusSaving: boolean;
    onClientChange: (clientId: string) => void;
    onToggleStatus: () => void;
}

export function DataTab({ form, errors, onField, isEdit, clients, selectedClient, projectStatus, statusSaving, onClientChange, onToggleStatus }: Props) {
    const { translate } = useApp();

    return (
        <Stack spacing={3}>
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1 }}>
                <CardContent sx={{ p: 3, '&:last-of-type': { pb: 3 } }}>
                    <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 2 }}>
                        {translate('projects.projectInfo')}
                    </Typography>
                    <Grid container spacing={2}>
                        {!isEdit && (
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    select
                                    label={`${translate('projects.client')} *`}
                                    value={form.clientId}
                                    onChange={(e) => onClientChange(e.target.value)}
                                    size="small"
                                    fullWidth
                                    error={!!errors.clientId}
                                    helperText={errors.clientId}
                                >
                                    <MenuItem value="">{translate('projects.selectClient')}</MenuItem>
                                    {clients.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}
                        {isEdit && (
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                                    {translate('projects.client')}
                                </Typography>
                                <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 1, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedClient?.name ?? form.clientId}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label={`${translate('projects.name')} *`}
                                value={form.name}
                                onChange={(e) => onField('name', e.target.value)}
                                size="small"
                                fullWidth
                                required
                                error={!!errors.name}
                                helperText={errors.name}
                                inputProps={{ maxLength: 200 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                label={`${translate('projects.abbreviation')} *`}
                                value={form.abbreviation}
                                onChange={(e) => onField('abbreviation', e.target.value.toUpperCase())}
                                size="small"
                                fullWidth
                                required
                                error={!!errors.abbreviation}
                                helperText={errors.abbreviation}
                                inputProps={{ maxLength: 10 }}
                            />
                        </Grid>
                        {isEdit && (
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                                    {translate('projects.status')}
                                </Typography>
                                <Chip
                                    label={translate(projectStatus === 'ATIVO' ? 'projectStatusLabels.ATIVO' : 'projectStatusLabels.INATIVO')}
                                    size="small"
                                    clickable
                                    disabled={statusSaving}
                                    onClick={onToggleStatus}
                                    sx={{
                                        fontWeight: 600,
                                        bgcolor: projectStatus === 'ATIVO' ? '#DCFCE7' : 'grey.200',
                                        color: projectStatus === 'ATIVO' ? '#166534' : 'text.secondary'
                                    }}
                                />
                            </Grid>
                        )}
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label={translate('projects.description')}
                                value={form.description}
                                onChange={(e) => onField('description', e.target.value)}
                                size="small"
                                fullWidth
                                multiline
                                rows={3}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                label={translate('projects.jiraProjectKey')}
                                value={form.jiraProjectKey}
                                onChange={(e) => onField('jiraProjectKey', e.target.value.toUpperCase())}
                                size="small"
                                fullWidth
                                error={!!errors.jiraProjectKey}
                                helperText={errors.jiraProjectKey}
                                inputProps={{ maxLength: 50 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 8 }}>
                            <TextField
                                label={translate('projects.jiraBaseUrl')}
                                value={form.jiraBaseUrl}
                                onChange={(e) => onField('jiraBaseUrl', e.target.value)}
                                size="small"
                                fullWidth
                                error={!!errors.jiraBaseUrl}
                                helperText={errors.jiraBaseUrl}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Stack>
    );
}
